// import pacchetti
import { useState, useEffect } from "react";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import { filesCloud, timestamp } from "../../firebase/config";

// import hooks
import { useFirestore } from "../../hooks/useFirestore";
import { useAuthContext } from "../../hooks/useAuthContext";

// import componenti
import ProjectFilter from "./ProjectFilter";

export default function ProjectFiles({ prj }) {
  const { updateDocument } = useFirestore("projects");
  const { user } = useAuthContext();
  const [fileSelezionato, setFileSelezionato] = useState(null);
  const [fileDescription, setFileDescription] = useState("");
  const [caricamentoFile, setCaricamentoFile] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fileError, setFileError] = useState(null);
  const [fileFilter, setFileFilter] = useState("tutti");
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredFiles, setFilteredFiles] = useState([]);
  const admin = user.uid === process.env.REACT_APP_ADMIN_ID;

  // Filtra i file in base al tipo e al termine di ricerca
  useEffect(() => {
    if (prj.files) {
      let result = [...prj.files];

      // Filtra per tipo di file
      if (fileFilter !== "tutti") {
        result = result.filter((file) => {
          const extension = file.fileName.split(".").pop().toLowerCase();
          switch (fileFilter) {
            case "pdf":
              return extension === "pdf";
            case "doc":
              return ["doc", "docx"].includes(extension);
            case "xls":
              return ["xls", "xlsx", "csv"].includes(extension);
            case "img":
              return ["jpg", "jpeg", "png", "gif", "svg"].includes(extension);
            case "altro":
              return ![
                "pdf",
                "doc",
                "docx",
                "xls",
                "xlsx",
                "csv",
                "jpg",
                "jpeg",
                "png",
                "gif",
                "svg",
              ].includes(extension);
            default:
              return true;
          }
        });
      }

      // Filtra per termine di ricerca
      if (searchTerm) {
        result = result.filter(
          (file) =>
            file.fileName.toLowerCase().includes(searchTerm) ||
            (file.description &&
              file.description.toLowerCase().includes(searchTerm))
        );
      }

      setFilteredFiles(result);
    }
  }, [prj.files, fileFilter, searchTerm]);

  const handleFilterChange = (value) => {
    setFileFilter(value);
  };

  const handleSearch = (term) => {
    setSearchTerm(term);
  };

  const handleFileChange = (e) => {
    setFileError(null);
    let selected = e.target.files[0];

    if (!selected) {
      setFileError("Seleziona un file");
      return;
    }
    if (selected.size > 10000000) {
      // 10MB
      setFileError("Il file deve essere inferiore a 10MB");
      return;
    }

    setFileSelezionato(selected);
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    setFileError(null);

    if (!fileSelezionato) {
      setFileError("Seleziona un file");
      return;
    }

    setCaricamentoFile(true);

    try {
      // Crea un riferimento allo storage
      const uploadPath = `projects/${prj.id}/${fileSelezionato.name}`;
      const storageRef = filesCloud.ref(uploadPath);

      // Carica il file
      const uploadTask = storageRef.put(fileSelezionato);

      // Monitora il progresso
      uploadTask.on(
        "state_changed",
        (snapshot) => {
          const percentage =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setProgress(percentage);
        },
        (error) => {
          setFileError(error.message);
          setCaricamentoFile(false);
        },
        async () => {
          // Caricamento completato
          const downloadURL = await uploadTask.snapshot.ref.getDownloadURL();

          // Crea un oggetto file
          const fileDoc = {
            fileName: fileSelezionato.name,
            filePath: uploadPath,
            downloadURL,
            uploadedAt: timestamp.fromDate(new Date()),
            uploadedBy: user.displayName,
            description: fileDescription,
          };

          // Aggiorna il documento del progetto
          await updateDocument(prj.id, {
            files: [...(prj.files || []), fileDoc],
          });

          setCaricamentoFile(false);
          setProgress(0);
          setFileSelezionato(null);
          setFileDescription("");
          document.getElementById("file-upload").value = "";
        }
      );
    } catch (err) {
      setFileError(err.message);
      setCaricamentoFile(false);
    }
  };

  const handleFileDelete = async (fileId, filePath) => {
    try {
      // Elimina il file dallo storage
      const storageRef = filesCloud.ref(filePath);
      await storageRef.delete();

      // Aggiorna il documento del progetto
      const updatedFiles = prj.files.filter((file) => file.id !== fileId);
      await updateDocument(prj.id, {
        files: updatedFiles,
      });
    } catch (err) {
      console.error("Errore durante l'eliminazione del file:", err);
    }
  };

  return (
    <div className="project-files">
      {/* Filtro per i file */}
      <ProjectFilter
        filtro={fileFilter}
        change={handleFilterChange}
        onSearch={handleSearch}
      />

      {/* Form per caricare un nuovo file */}
      <form className="file-upload-form" onSubmit={handleFileUpload}>
        <label>
          <span>Carica un nuovo file:</span>
          <input
            type="file"
            id="file-upload"
            onChange={handleFileChange}
            disabled={caricamentoFile}
          />
        </label>
        <label>
          <span>Descrizione:</span>
          <input
            type="text"
            value={fileDescription}
            onChange={(e) => setFileDescription(e.target.value)}
            disabled={caricamentoFile}
          />
        </label>
        <button
          className="btn"
          disabled={caricamentoFile || !fileSelezionato}
        >
          {caricamentoFile ? "Caricamento in corso..." : "Carica File"}
        </button>
        {caricamentoFile && (
          <div className="progress-bar">
            <div
              className="progress"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        )}
        {fileError && <p className="error">{fileError}</p>}
      </form>

      {/* Lista dei file */}
      {filteredFiles.length > 0 ? (
        <div className="files-list">
          {filteredFiles.map((file) => (
            <div key={file.id} className="file-item">
              <div className="file-info">
                <p className="file-name">{file.fileName}</p>
                <p className="file-description">
                  {file.description || "Nessuna descrizione"}
                </p>
                <p className="file-date">
                  Caricato il{" "}
                  {format(file.uploadedAt.toDate(), "d MMM y HH:mm", {
                    locale: it,
                  })}
                </p>
              </div>
              <div className="file-actions">
                <a
                  href={file.downloadURL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-small"
                >
                  Scarica
                </a>
                {admin && (
                  <button
                    className="btn btn-small btn-danger"
                    onClick={() => handleFileDelete(file.id, file.filePath)}
                  >
                    Elimina
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <p>Nessun file trovato con i filtri selezionati.</p>
      )}
    </div>
  );
}